import { MediaService } from '@shared/services/media.service';
import { MediaFile } from '@shared/types/media.types';
import api from '../../../shared/services/api';

export interface PDFProcessingRequest {
  name: string;
  pdfUrl: string;
  answersPdfUrl?: string;
  startPage?: number;
  answerPages?: number;
  alternativeProcessing?: boolean;
  currentFormat?: boolean;
  pdfStartPage?: number;
  pdfEndPage?: number;
  diagnosticTest?: boolean;
}

export interface PDFProcessingResponse {
  message: string;
  name: string;
  testId?: string;
  processedTest?: any;
  processingId?: string;
  streamUrl?: string;
}

export interface ProcessingStatus {
  status:
    | 'uploading'
    | 'processing'
    | 'completed'
    | 'error'
    | 'loading'
    | 'analyzing'
    | 'converting'
    | 'processing_answers'
    | 'processing_content'
    | 'matching'
    | 'initializing';
  progress: number;
  message: string;
  error?: string;
  testId?: string;
  processedTest?: any;
  shouldClose?: boolean;
}

export class PDFProcessingService {
  private static readonly BASE_URL = '/pdf';

  /**
   * Upload PDF file and process it
   */
  static async uploadAndProcessPDF(
    userId: string,
    file: MediaFile,
    name: string,
    options: {
      startPage?: number;
      answerPages?: number;
      alternativeProcessing?: boolean;
      currentFormat?: boolean;
      answersMediaFile?: MediaFile | null;
      pdfStartPage?: number;
      pdfEndPage?: number;
      diagnosticTest?: boolean;
    } = {},
    onStatusUpdate?: (status: ProcessingStatus) => void
  ): Promise<PDFProcessingResponse> {
    try {
      // Step 1: Upload PDF to Firebase Storage
      onStatusUpdate?.({
        status: 'uploading',
        progress: 0,
        message: 'Uploading PDF file...',
      });

      const pdfUrl = await MediaService.uploadMedia(
        userId,
        file,
        {
          maxSize: 50 * 1024 * 1024, // 50MB
          acceptedTypes: ['application/pdf'],
        },
        (progress) => {
          onStatusUpdate?.({
            status: 'uploading',
            progress: progress.progress,
            message: 'Uploading PDF file...',
            error: progress.error,
          });
        }
      );

      // Step 2: Upload answers PDF if provided
      let answersPdfUrl: string | undefined;
      if (options.answersMediaFile) {
        onStatusUpdate?.({
          status: 'uploading',
          progress: 50,
          message: 'Uploading answers PDF...',
        });

        answersPdfUrl = await MediaService.uploadMedia(
          userId,
          options.answersMediaFile,
          {
            maxSize: 50 * 1024 * 1024, // 50MB
            acceptedTypes: ['application/pdf'],
          },
          (progress) => {
            onStatusUpdate?.({
              status: 'uploading',
              progress: 50 + progress.progress / 2, // 50-100%
              message: 'Uploading answers PDF...',
              error: progress.error,
            });
          }
        );
      }

      // Step 3: Process the uploaded PDF(s)
      onStatusUpdate?.({
        status: 'processing',
        progress: 0,
        message: 'Processing PDF content...',
      });

      const request: PDFProcessingRequest = {
        name,
        pdfUrl,
        answersPdfUrl,
        startPage: options.startPage || 1,
        answerPages: options.answerPages || 1,
        alternativeProcessing: options.alternativeProcessing || false,
        currentFormat: options.currentFormat || false,
        pdfStartPage: options.pdfStartPage || 1,
        pdfEndPage: options.pdfEndPage || 0,
        diagnosticTest: options.diagnosticTest || false,
      };

      // Use streaming API if status updates are requested
      if (onStatusUpdate) {
        return await this.processWithStreaming(request, onStatusUpdate);
      } else {
        // Use traditional API if no status updates are needed
        const response = await api.post<PDFProcessingResponse>(
          `${this.BASE_URL}/process-url`,
          request
        );

        return response.data;
      }
    } catch (error: any) {
      onStatusUpdate?.({
        status: 'error',
        progress: 0,
        message: 'Error processing PDF',
        error: error.message || 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Get PDF file URL for a test
   */
  static async getTestPDFUrl(testId: string): Promise<string> {
    try {
      const response = await api.get(`/tests/${testId}/pdf`);
      return response.data.pdfUrl;
    } catch (error: any) {
      throw new Error(error.message || 'Error getting PDF URL');
    }
  }

  /**
   * Process PDF with streaming status updates
   */
  private static async processWithStreaming(
    request: PDFProcessingRequest,
    onStatusUpdate: (status: ProcessingStatus) => void
  ): Promise<PDFProcessingResponse> {
    try {
      // Start processing with streaming enabled
      const response = await api.post<PDFProcessingResponse>(
        `${this.BASE_URL}/process`,
        request,
        {
          params: { stream: 'true' },
        }
      );

      const { processingId, streamUrl } = response.data;

      if (!processingId || !streamUrl) {
        throw new Error('Invalid streaming response from server');
      }

      // Create a promise that will resolve when processing is complete
      return new Promise((resolve, reject) => {
        // Connect to SSE endpoint
        const eventSource = new EventSource(
          `${api.defaults.baseURL}${streamUrl}`
        );
        let testResult: PDFProcessingResponse | null = null;

        // Handle regular status updates
        eventSource.addEventListener('message', (event) => {
          try {
            const data = JSON.parse(event.data);
            const { status, progress } = data;

            // Map status to user-friendly message
            const messageMap: Record<string, string> = {
              initializing: 'Initializing processing...',
              loading: 'Loading PDF...',
              analyzing: 'Analyzing document structure...',
              converting: 'Processing pages...',
              processing_answers: 'Processing answer sheet...',
              processing_content: 'Processing questions and options...',
              matching: 'Matching questions with answers...',
              completed: 'PDF processed successfully!',
              error: 'Error processing PDF',
            };

            onStatusUpdate({
              status: status,
              progress: progress,
              message: messageMap[status] || `Processing (${status})...`,
            });

            // Do not close connection here, wait for explicit close event from server
            // The server will send a close event after sending all data
          } catch (err) {
            console.error('Error parsing SSE message:', err);
          }
        });

        // Handle result event (contains test ID)
        eventSource.addEventListener('result', (event) => {
          try {
            const data = JSON.parse(event.data);
            testResult = {
              message: 'PDF processed successfully',
              name: request.name,
              testId: data.testId,
              processedTest: data.processedTest,
            };

            // Update status with test ID
            onStatusUpdate({
              status: 'completed',
              progress: 100,
              message: 'PDF processed successfully!',
              testId: data.testId,
              processedTest: data.processedTest, // Include processed test data
            });

            // Don't close the connection yet, wait for the close event
            // The backend will send a close event after sending the result
            // This ensures we receive all data before closing
          } catch (err) {
            console.error('Error parsing result event:', err);
          }
        });

        // Handle error event
        eventSource.addEventListener('error', (event) => {
          // Check if this is a connection close event
          if (eventSource.readyState === EventSource.CLOSED) {
            clearTimeout(timeout);
            if (testResult) {
              resolve(testResult);
              return;
            }
          }

          try {
            // For error events with data
            if (event instanceof MessageEvent && event.data) {
              const data = JSON.parse(event.data);
              onStatusUpdate({
                status: 'error',
                progress: 0,
                message: 'Error processing PDF',
                error: data.details || data.error || 'Unknown error',
              });
              eventSource.close();
              reject(
                new Error(data.details || data.error || 'Error processing PDF')
              );
            } else if (eventSource.readyState !== EventSource.CLOSED) {
              // For connection errors (only if not already closed)
              onStatusUpdate({
                status: 'error',
                progress: 0,
                message: 'Connection error',
                error: 'Connection to server was lost',
              });
              eventSource.close();
              reject(new Error('Connection error'));
            }
          } catch (err) {
            console.error('Error handling error event:', err);
            eventSource.close();
            reject(err);
          }
        });

        // Set timeout to prevent hanging connections
        const timeout = setTimeout(() => {
          if (eventSource.readyState !== EventSource.CLOSED) {
            eventSource.close();
            onStatusUpdate({
              status: 'error',
              progress: 0,
              message: 'Processing timeout',
              error: 'Processing took too long to complete',
            });
            reject(new Error('Processing timeout'));
          }
        }, 15 * 60 * 1000); // 15 minutes timeout

        // Handle explicit close event from server
        eventSource.addEventListener('close', (event) => {
          clearTimeout(timeout);

          try {
            if (event instanceof MessageEvent && event.data) {
              const data = JSON.parse(event.data);
              if (data.shouldClose) {
                console.log('Server requested connection close');

                // Update status to indicate closing
                onStatusUpdate({
                  status: data.status,
                  progress: data.progress,
                  message: 'Processing completed successfully!',
                  shouldClose: true,
                  testId: testResult?.testId,
                });

                // Close the connection and resolve if we have a result
                eventSource.close();
                if (testResult) {
                  resolve(testResult);
                }
              }
            }
          } catch (err) {
            console.error('Error handling close event:', err);
          }
        });

        // Note: Connection close is now handled in the main error event handler above
      });
    } catch (error) {
      onStatusUpdate({
        status: 'error',
        progress: 0,
        message: 'Error processing PDF',
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }
}
